// Localização: frontend/src/components/PlanoMedico/PlanoMedicoList.js

import React, { useState } from 'react';
import LoadingIndicator from '../Common/LoadingIndicator';
import SimpleSearchBar from '../Common/SimpleSearchBar';
import { SquarePen, Trash2, XCircle } from 'lucide-react';

const PlanoMedicoList = ({
    planos,
    isLoading,
    onEdit,
    onDelete,
    onCancel,
    canEdit,
    canDelete,
    canCancel,
    hideSearch = false
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    // Função para formatar a frequência para exibição
    const formatFrequencia = (plano) => {
        if (plano.frequencia_tipo === 'Recorrente' && plano.frequencia_intervalo_horas) {
            return `A cada ${plano.frequencia_intervalo_horas} horas`;
        }
        if (plano.frequencia_tipo === 'Avulsa') {
            return 'Dose Única';
        }
        if (plano.frequencia_tipo === 'Diária') {
             return 'Diário (Horário Fixo)';
        }
        return plano.frequencia_tipo;
    };

    // Filtro de Busca (pelo nome do medicamento ou dose)
    const filteredPlanos = planos.filter(plano => 
        plano.nome_medicamento?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plano.dose?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="mt-6">
            {!hideSearch && (
                <div className="flex justify-between items-center mb-4">
                    <div className="w-1/3 mr-4">
                        <SimpleSearchBar 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar por medicamento ou dose..."
                        />
                    </div>
                </div>
            )}

            {isLoading ? (
                <LoadingIndicator />
            ) : (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Medicamento</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dosagem</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequência</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Início/Base</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredPlanos.length > 0 ? filteredPlanos.map((plano) => (
                                <tr key={plano.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {plano.nome_medicamento || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {plano.dose} {plano.unidade_medida}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatFrequencia(plano)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(plano.data_inicio).toLocaleDateString('pt-BR')} às {plano.horario_base.substring(0, 5)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            {canEdit && (
                                                <button
                                                    onClick={() => onEdit(plano)}
                                                    className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors duration-200"
                                                    title="Editar"
                                                >
                                                    <SquarePen className="w-4 h-4" />
                                                </button>
                                            )}
                                            {canCancel && onCancel && (() => {
                                                // Mostra cancelar apenas se o plano estiver ativo (sem data_fim ou data_fim no futuro)
                                                const hoje = new Date();
                                                hoje.setHours(0, 0, 0, 0);
                                                const dataFim = plano.data_fim ? new Date(plano.data_fim) : null;
                                                const isAtivo = !dataFim || dataFim >= hoje;
                                                
                                                return isAtivo ? (
                                                    <button
                                                        onClick={() => onCancel(plano)}
                                                        className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors duration-200"
                                                        title="Cancelar"
                                                    >
                                                        <XCircle className="w-4 h-4" />
                                                    </button>
                                                ) : null;
                                            })()}
                                            {canDelete && (
                                                <button
                                                    onClick={() => onDelete(plano.id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                                    title="Excluir"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                                        {searchTerm ? `Nenhum plano de medicação encontrado para "${searchTerm}".` : 'Nenhum plano de medicação cadastrado.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default PlanoMedicoList;